import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowRight, CheckCircle, Clock, ClipboardList, Trophy } from "lucide-react";

const CertificationPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-24">
        {/* Hero Banner */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1587613754760-cd9a285831b3?auto=format&fit=crop&w=1920&q=80"
              alt="Conscious Business Certification Assessment Process"
              className="w-full h-full object-cover"
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-slate-900/65 to-purple-900/70"></div>
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                Your certification journey,{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                  end to end
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-12 leading-relaxed">
                Understand tiers, eligibility, timelines, and evidence expectations—all designed to be thorough, fair, and practical.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a href="/signup">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-10 py-6">
                    Check Eligibility
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <a href="/signup">
                  <Button variant="outline" size="lg" className="border-2 border-white/40 text-white hover:bg-white hover:text-slate-900 text-lg px-10 py-6">
                    Get Certified
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Certification Process */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center">
                How CNESS Certification Works
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    step: "01",
                    title: "Discover & Assess",
                    description: "Complete our eligibility check and comprehensive assessment across all 6 pillars of conscious enterprise.",
                    icon: ClipboardList,
                    timeline: "2-4 weeks"
                  },
                  {
                    step: "02", 
                    title: "Review & Verification",
                    description: "Our expert reviewers evaluate your submission, evidence, and practices against certification criteria.",
                    icon: CheckCircle,
                    timeline: "3-6 weeks"
                  },
                  {
                    step: "03",
                    title: "Certification & Recognition",
                    description: "Receive your tier certification, digital badge, and listing in our verified directory.",
                    icon: Trophy,
                    timeline: "1 week"
                  }
                ].map((step, index) => (
                  <Card key={index} className="relative group hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-8">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-amber-500 mb-4">{step.step}</div>
                        
                        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center group-hover:from-amber-500 group-hover:to-amber-600 transition-all duration-300">
                          <step.icon className="h-8 w-8 text-amber-600 group-hover:text-white transition-colors duration-300" />
                        </div>
                        
                        <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                        <p className="text-gray-600 mb-4">{step.description}</p>
                        
                        <div className="flex items-center justify-center">
                          <Clock className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-500">{step.timeline}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Certification Tiers */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
                Certification Tiers
              </h2>
              <p className="text-xl text-gray-600 mb-16 text-center max-w-3xl mx-auto">
                Three levels of certification designed to recognize organizations at different stages of their conscious business journey.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    name: "Aspiring",
                    badge: "Entry Level",
                    badgeImage: "https://customer-assets.emergentagent.com/job_premier-b2b-site/artifacts/96hhy209_Badge%20Level%201.png",
                    description: "Perfect for organizations beginning their conscious business journey with documented intent to improve.",
                    features: [
                      "Basic assessment across 6 pillars",
                      "Entry-level certification badge", 
                      "Directory listing",
                      "Community access",
                      "Annual renewal"
                    ],
                    popular: false
                  },
                  {
                    name: "Inspired",
                    badge: "Advanced",
                    badgeImage: "https://customer-assets.emergentagent.com/job_premier-b2b-site/artifacts/r11ziiyu_Badge%20Level%202.png",
                    description: "For organizations demonstrating strong maturity across pillars with evidence and consistent results.",
                    features: [
                      "Comprehensive assessment",
                      "Advanced certification badge",
                      "Best practice sharing platform",
                      "Mentorship opportunities",
                      "Priority support",
                      "Enhanced directory profile"
                    ],
                    popular: true
                  },
                  {
                    name: "Luminary",
                    badge: "Leadership",
                    badgeImage: "https://customer-assets.emergentagent.com/job_premier-b2b-site/artifacts/e1lw588c_Badge%20Level%203.png",
                    description: "For industry leaders with exemplary performance and measurable, sustained impact at scale.",
                    features: [
                      "Expert-level assessment",
                      "Leadership certification badge",
                      "Thought leadership platform",
                      "Strategic partnership opportunities",
                      "Executive networks access",
                      "Featured directory placement"
                    ],
                    popular: false
                  }
                ].map((tier, index) => (
                  <Card key={index} className={`relative ${tier.popular ? 'ring-2 ring-amber-500 scale-105' : ''} hover:shadow-xl transition-all duration-300`}>
                    {tier.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">Most Popular</Badge>
                      </div>
                    )}
                    
                    <CardContent className="p-8 text-center">
                      <div className="w-20 h-20 mx-auto mb-6">
                        <img src={tier.badgeImage} alt={`${tier.name} Badge`} className="w-full h-full object-contain" />
                      </div>
                      
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                      <Badge variant="outline" className="mb-4">{tier.badge}</Badge>
                      <p className="text-gray-600 mb-6">{tier.description}</p>
                      
                      <div className="space-y-3 mb-8 text-left">
                        {tier.features.map((feature, fIndex) => (
                          <div key={fIndex} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      <a href="/signup">
                        <Button className={`w-full ${tier.popular ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' : 'bg-gray-900 hover:bg-gray-800'} text-white`}>
                          Apply for {tier.name}
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Requirements Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                Certification Requirements
              </h2>
              <p className="text-xl text-gray-600 mb-12">
                All certifications are evaluated across our 6 Pillars framework with evidence-based assessment.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                {[
                  "Documented policies and procedures",
                  "Evidence of implementation",
                  "Stakeholder feedback and testimonials", 
                  "Performance metrics and outcomes",
                  "Continuous improvement plans",
                  "Leadership commitment demonstration",
                  "Transparency in reporting",
                  "Community and environmental impact"
                ].map((requirement, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{requirement}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-12">
                <a href="/signup">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white">
                    Start Your Application
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CertificationPage;